import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { CloseIcon } from '../common/CloseIcon';
import { Trans, useTranslation } from 'react-i18next';
import './OffMarketBuyAndSellDetailsModal.scss'; // We'll create this stylesheet
import ExpanderArrow from './ExpanderArrow';
import { CSSTransition } from 'react-transition-group';
import OffMarketSellIcon from '../FeedEvents/OffMarketSellIcon';
import OffMarketBuyIcon from '../FeedEvents/OffMarketBuyIcon';
import { getCookie } from '../utils/utils';

type OffMarketAction = 'buy' | 'sell';

interface ImageData {
  id: string;
  url: string;
  thumbnailUrl: string;
  uploadedAt?: string;
  order?: number;
  isUploading?: boolean;
  uploadProgress?: number;
  error?: string;
}

export interface PropertyDetails {
  description?: string;
  size?: number;
  rooms?: string;
  floor?: number;
  hasElevator?: boolean;
  hasBalcony?: boolean;
  renovationStatus?: string;
  images?: ImageData[];
}

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (propertyDetails?: PropertyDetails) => void;
  actionType: OffMarketAction;
  isLoading: boolean;
  isContractor?: boolean;
  isLoggedIn?: boolean;
  isLandingPage?: boolean;
  isEditMode?: boolean;
  initialData?: PropertyDetails;
  willingnessId?: string;
  setIsEditModalLoading?: (loading: boolean) => void;
};

const MAX_IMAGES = 30;

const OffMarketBuyAndSellDetailsModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onConfirm,
  actionType,
  isLoading,
  isContractor,
  isLoggedIn,
  isLandingPage,
  isEditMode,
  initialData,
  willingnessId,
  setIsEditModalLoading
}) => {
  const { t } = useTranslation();
  const [description, setDescription] = useState<string>('');
  const [size, setSize] = useState<string>('');
  const [rooms, setRooms] = useState<string>('');
  const [floor, setFloor] = useState<string>('');
  const [sizeError, setSizeError] = useState<string>('');
  const [hasElevator, setHasElevator] = useState<boolean>(false);
  const [hasBalcony, setHasBalcony] = useState<boolean>(false);
  const [renovationStatus, setRenovationStatus] = useState<string>('');
  const [isDetailsExpanded, setIsDetailsExpanded] = useState<boolean>(isEditMode ? true : false);
  const [images, setImages] = useState<ImageData[]>([]);
  const [uploadError, setUploadError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Initialize state with initial data when in edit mode
  useEffect(() => {
    if (isEditMode && initialData) {
      setDescription(initialData.description || '');
      setSize(initialData.size ? initialData.size.toString() : '');
      setRooms(initialData.rooms || '');
      setFloor(typeof initialData.floor === 'number' ? initialData.floor.toString() : initialData.floor || '');
      setHasElevator(initialData.hasElevator || false);
      setHasBalcony(initialData.hasBalcony || false);
      setRenovationStatus(initialData.renovationStatus || '');
      setImages(initialData.images || []);
    }
  }, [isEditMode, initialData]);

  if (!isOpen) return null;

  const handleSubmit = () => {
    // Frontend validation: size cannot be 0 if provided
    if (size.trim() !== '' && Number(size) === 0) {
      setSizeError(t('building.offMarket.modal.sizeMustBeGreaterThanZero'));
      return;
    }
    setSizeError('');
    const propertyDetails: PropertyDetails = {};

    propertyDetails.description = description;
    propertyDetails.size = (typeof size === 'string' && size.trim() === '') ? undefined : Number(size);
    propertyDetails.rooms = rooms;
    propertyDetails.floor = (typeof floor === 'string' && floor.trim() === '') ? undefined : Number(floor);
    propertyDetails.hasElevator = hasElevator;
    propertyDetails.hasBalcony = hasBalcony;
    propertyDetails.renovationStatus = renovationStatus;

    // Only include successfully uploaded images (those that have an id and no error)
    const validImages = images.filter(img => img.id && !img.error && !img.isUploading);
    if (validImages.length > 0) {
      propertyDetails.images = validImages;
    }

    // Reset form
    if (isLoggedIn) {
      setDescription('');
      setSize('');
      setRooms('');
      setFloor('');
      setHasElevator(false);
      setHasBalcony(false);
      setRenovationStatus('');
      setImages([]);
      setUploadError('');
    }

    onConfirm(propertyDetails);
  };

  const resetForm = () => {
    setDescription('');
    setSize('');
    setRooms('');
    setFloor('');
    setHasElevator(false);
    setHasBalcony(false);
    setRenovationStatus('');
    setIsDetailsExpanded(false);
    setImages([]);
    setUploadError('');
    onClose();
  };

  const toggleDetailsExpansion = () => {
    setIsDetailsExpanded(!isDetailsExpanded);
  };

  const handleImageUpload = async (files: FileList, setIsEditModalLoading: (loading: boolean) => void) => {
    // Convert FileList to array
    const fileArray = Array.from(files);

    // Check if adding new files would exceed the limit
    if (images.length + fileArray.length > MAX_IMAGES) {
      setUploadError(t('building.offMarket.modal.imageLimitExceeded', { max: MAX_IMAGES }));
      return;
    }

    // Filter only image files
    const imageFiles = fileArray.filter(file => file.type.startsWith('image/'));

    // Create temporary image entries with upload status
    const newImagesWithStatus = imageFiles.map(file => ({
      id: '', // Will be assigned after upload
      url: URL.createObjectURL(file), // Create temporary URL for preview
      thumbnailUrl: URL.createObjectURL(file),
      isUploading: true,
      uploadProgress: 0,
      error: undefined
    }));

    // Add to images state
    setImages(prev => [...prev, ...newImagesWithStatus]);

    // Upload each image
    imageFiles.forEach(async (file, index) => {
      setIsEditModalLoading(true);
      const formData = new FormData();
      formData.append('image', file);

      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/upload-property-image`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            // No Content-Type header - browser will set it with boundary for FormData
            'X-CSRF-TOKEN': getCookie('csrf_access_token'),
          },
          body: formData,
        });

        if (!response.ok) {
          throw new Error(`Upload failed with status ${response.status}`);
        }

        const data = await response.json();

        // Update image data with info from server
        setImages(prev => {
          const newImages = [...prev];
          const targetIndex = prev.length - imageFiles.length + index;

          if (newImages[targetIndex]) {
            newImages[targetIndex] = {
              id: data.id,
              url: data.url,
              thumbnailUrl: data.thumbnailUrl,
              isUploading: false,
              uploadProgress: 100
            };
          }

          return newImages;
        });

        // Revoke object URL to avoid memory leaks
        URL.revokeObjectURL(newImagesWithStatus[index].url);
        URL.revokeObjectURL(newImagesWithStatus[index].thumbnailUrl);

      } catch (error) {
        // Update image with error status
        setImages(prev => {
          const newImages = [...prev];
          const targetIndex = prev.length - imageFiles.length + index;

          if (newImages[targetIndex]) {
            newImages[targetIndex] = {
              ...newImages[targetIndex],
              isUploading: false,
              error: error instanceof Error ? error.message : String(error)
            };
          }

          return newImages;
        });

        // Revoke object URL to avoid memory leaks
        URL.revokeObjectURL(newImagesWithStatus[index].url);
        URL.revokeObjectURL(newImagesWithStatus[index].thumbnailUrl);
      }
      setIsEditModalLoading(false);
    });

    setUploadError('');
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>, setIsEditModalLoading: (loading: boolean) => void) => {
    if (e.target.files && e.target.files.length > 0) {
      handleImageUpload(e.target.files, setIsEditModalLoading);
      // Reset the input value so the same file can be selected again
      e.target.value = '';
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, setIsEditModalLoading: (loading: boolean) => void) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleImageUpload(e.dataTransfer.files, setIsEditModalLoading);
    }
  };

  const removeImage = (index: number) => {
    const imageToRemove = images[index];

    // If the image has temporary URLs (from createObjectURL), revoke them
    if (imageToRemove.isUploading || imageToRemove.error) {
      URL.revokeObjectURL(imageToRemove.url);
      URL.revokeObjectURL(imageToRemove.thumbnailUrl);
    }

    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Use createPortal to render outside the parent component hierarchy
  return ReactDOM.createPortal(
    <div className={`off-market-modal-overlay ${isLandingPage ? 'off-market-modal-overlay--landing-page' : ''}`}>
      <div className="off-market-modal">
        <div className="off-market-modal__close-button-wrapper">
          <button
            className="off-market-modal__close-button"
            onClick={resetForm}
            disabled={isLoading}
          >
            <CloseIcon size={20} />
          </button>
        </div>
        <div className="off-market-modal__header">
          <h2 className="off-market-modal__title">
            {actionType === 'sell' ? (
              <OffMarketSellIcon
                width="26px"
                height="26px"
                className="off-market-modal__sell-icon"
              />
            ) : (
              <OffMarketBuyIcon
                width="26px"
                height="26px"
                className="off-market-modal__buy-icon"
              />
            )}
            {isEditMode ? (
              <Trans i18nKey="building.offMarket.modal.edit.title" />
            ) : (
              <Trans
                i18nKey={`building.offMarket.modal.${actionType}.title`}
              />
            )}
          </h2>
        </div>

        <div className="off-market-modal__content">
          <p className={`off-market-modal__description ${isEditMode ? 'off-market-modal__description--edit' : ''}`}>
            {isEditMode ? (
              <Trans
                i18nKey={`building.offMarket.modal.edit.${actionType}Description`}
              />
            ) : (
              isContractor ?
                <Trans
                  i18nKey={`building.offMarket.modal.contractor.buyDescription`}
                />
                :
                <Trans
                  i18nKey={`building.offMarket.modal.${actionType}.description`}
                />
            )}
          </p>

          <div className="off-market-modal__form">
            {/*             <h3 className="off-market-modal__form-title">
              <Trans i18nKey="building.offMarket.modal.detailsOptional" />
            </h3> */}

            {!isEditMode && (
              <div className="off-market-modal__expander">
                <button
                  onClick={toggleDetailsExpansion}
                  className="off-market-modal__expander-button"
                >
                  <Trans i18nKey="building.offMarket.modal.additionalDetails" />
                  <ExpanderArrow isExpanded={isDetailsExpanded} />
                </button>
              </div>
            )}


            <CSSTransition
              in={isDetailsExpanded}
              timeout={300}
              classNames="details-expand"
              unmountOnExit
            >
              <div className="off-market-modal__additional-details">

                <div className="off-market-modal__form-field">
                  <label htmlFor="property-description">
                    <Trans i18nKey="building.offMarket.modal.description" />
                  </label>
                  <textarea
                    id="property-description"
                    className="off-market-modal__textarea"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder={actionType === 'buy' ? t('building.offMarket.modal.buyingInterestDescriptionPlaceholder') : t('building.offMarket.modal.sellingInterestDescriptionPlaceholder')}
                    rows={3}
                    maxLength={450}
                  />
                  <span className="off-market-modal__textarea-counter">{description.length} / 450</span>
                </div>
                <div className="off-market-modal__form-row">
                  <div className="off-market-modal__form-field off-market-modal__form-field--half">
                    <label htmlFor="property-size">
                      {actionType === 'buy' ? <Trans i18nKey="building.offMarket.modal.sizeClass" /> : <Trans i18nKey="building.offMarket.modal.size" />}
                    </label>
                    <input
                      id="property-size"
                      type="number"
                      className="off-market-modal__input"
                      value={size}
                      onChange={(e) => {
                        const next = e.target.value;
                        setSize(next);
                        if (next.trim() === '') {
                          setSizeError('');
                        } else if (Number(next) === 0) {
                          setSizeError(t('building.offMarket.modal.sizeMustBeGreaterThanZero'));
                        } else {
                          setSizeError('');
                        }
                      }}
                      placeholder={t('building.offMarket.modal.sizePlaceholder')}
                      min="1"
                    />
                    {sizeError && (
                      <div className="off-market-modal__input-error" role="alert">
                        {sizeError}
                      </div>
                    )}
                  </div>

                  <div className="off-market-modal__form-field off-market-modal__form-field--half">
                    <label htmlFor="property-rooms">
                      <Trans i18nKey="building.offMarket.modal.roomsLabel" />
                    </label>
                    <select
                      id="property-rooms"
                      className="off-market-modal__select"
                      value={rooms}
                      onChange={(e) => setRooms(e.target.value)}
                    >
                      <option value="">
                        {t('building.offMarket.modal.selectRooms')}
                      </option>
                      <option value="1">1</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                      <option value="4">4</option>
                      <option value="5+">5+</option>
                    </select>
                  </div>
                </div>
                {!isContractor && (
                  <>
                    <div className="off-market-modal__form-row">
                      <div className="off-market-modal__form-field off-market-modal__form-field--half">
                        <label htmlFor="property-floor">
                          <Trans i18nKey="building.offMarket.modal.floor" />
                        </label>
                        <input
                          id="property-floor"
                          type="number"
                          className="off-market-modal__input"
                          value={floor}
                          onChange={(e) => setFloor(e.target.value)}
                          placeholder={t('building.offMarket.modal.floorPlaceholder')}
                          min="0"
                        />
                      </div>

                      <div className="off-market-modal__form-field off-market-modal__form-field--half">
                        <label htmlFor="property-renovation">
                          <Trans i18nKey="building.offMarket.modal.renovation" />
                        </label>
                        <select
                          id="property-renovation"
                          className="off-market-modal__select"
                          value={renovationStatus}
                          onChange={(e) => setRenovationStatus(e.target.value)}
                        >
                          <option value="">
                            {t('building.offMarket.modal.selectRenovation')}
                          </option>
                          <option value="recently_renovated">
                            {t('building.offMarket.modal.recentlyRenovated')}
                          </option>
                          <option value="good_condition">
                            {t('building.offMarket.modal.goodCondition')}
                          </option>
                          <option value="needs_renovation">
                            {t('building.offMarket.modal.needsRenovation')}
                          </option>
                        </select>
                      </div>
                    </div>

                    <div className="off-market-modal__form-row off-market-modal__form-row--checkboxes">
                      <div className="off-market-modal__form-field off-market-modal__form-field--checkbox">
                        <input
                          id="property-elevator"
                          type="checkbox"
                          checked={hasElevator}
                          onChange={(e) => setHasElevator(e.target.checked)}
                        />
                        <label htmlFor="property-elevator">
                          <Trans i18nKey="building.offMarket.modal.hasElevator" />
                        </label>
                      </div>

                      <div className="off-market-modal__form-field off-market-modal__form-field--checkbox">
                        <input
                          id="property-balcony"
                          type="checkbox"
                          checked={hasBalcony}
                          onChange={(e) => setHasBalcony(e.target.checked)}
                        />
                        <label htmlFor="property-balcony">
                          <Trans i18nKey="building.offMarket.modal.hasBalcony" />
                        </label>
                      </div>
                    </div>

                    {/* Image Upload Section */}
                    {actionType === 'sell' && (
                      <div
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, setIsEditModalLoading)}
                        onClick={triggerFileInput}
                        className="off-market-modal__image-upload-section">
                        <h4 className="off-market-modal__image-upload-title">
                          <Trans i18nKey="building.offMarket.modal.addPropertyImages" />
                          <span className="off-market-modal__image-count">
                            {images.length}/{MAX_IMAGES}
                          </span>
                        </h4>

                        <div
                          className="off-market-modal__image-dropzone"
                        >
                          <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            accept="image/*"
                            className="off-market-modal__file-input"
                            onChange={(e) => handleFileInputChange(e, setIsEditModalLoading)}
                          />
                          <div className="off-market-modal__dropzone-content">
                            <div className="off-market-modal__upload-icon">
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 5V19M12 5L7 10M12 5L17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </div>
                            <p className="off-market-modal__dropzone-text">
                              <Trans i18nKey="building.offMarket.modal.dragImagesHere" />
                            </p>
                          </div>
                        </div>

                        {uploadError && (
                          <div className="off-market-modal__upload-error">
                            {uploadError}
                          </div>
                        )}

                        {images.length > 0 && (
                          <div className="off-market-modal__image-preview-grid">
                            {images.map((image, index) => (
                              <div
                                key={index}
                                className={`off-market-modal__image-preview ${image.isUploading ? 'is-uploading' : ''} ${image.error ? 'has-error' : ''}`}
                              >
                                <img
                                  src={image.thumbnailUrl}
                                  alt={t('building.offMarket.modal.propertyImage')}
                                  className="off-market-modal__preview-img"
                                />

                                <button
                                  className="off-market-modal__image-remove"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeImage(index);
                                  }}
                                  title={t('building.offMarket.modal.removeImage')}
                                >
                                  <CloseIcon size={14} />
                                </button>

                                {image.isUploading && (
                                  <div className="off-market-modal__image-upload-progress">
                                    <div className="off-market-modal__progress-indicator"></div>
                                  </div>
                                )}

                                {image.error && (
                                  <div className="off-market-modal__image-error-overlay">
                                    <span className="off-market-modal__image-error-text">
                                      {t('building.offMarket.modal.uploadFailed')}
                                    </span>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </>)
                }
              </div>
            </CSSTransition>
          </div>
        </div>

        <div className="off-market-modal__footer">
          <button
            className="off-market-modal__button off-market-modal__button--cancel"
            onClick={resetForm}
            disabled={isLoading}
          >
            <Trans i18nKey="building.offMarket.modal.cancel" />
          </button>
          <button
            className="off-market-modal__button off-market-modal__button--confirm"
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="off-market-modal__button-spinner"></div>
            ) : (
              isEditMode ? (
                <Trans i18nKey="building.offMarket.modal.update" />
              ) : (
                <Trans i18nKey="building.offMarket.modal.confirm" />
              )
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body // Render directly to the body element
  );
};

export default OffMarketBuyAndSellDetailsModal;